import { Component, Vue } from 'vue-facing-decorator';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Memo, Check, Delete, EditPen, CirclePlusFilled, Ticket, View, Checked } from '@element-plus/icons-vue';
import Header from '@/components/Header/Header.vue';
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs.vue';
import Navigation from '@/components/Navigation/Navigation.vue';
import NavigationIndex from "@/models/EnumNavigationIndex";
import ApiService from '@/services/ApiService';
import AuthService from '@/services/AuthService';

@Component(
{
    components:
    {
        Header,
        Breadcrumbs,
        Navigation,
		Memo,
		Check,
		EditPen,
		Delete,
		CirclePlusFilled,
        Ticket,
        View,
        Checked
    },
    enums:
    {
        NavigationIndex
    }
})

export default class TicketList extends Vue 
{
    private activeName : string = "1";  
    private customerId : number = 0;
    private isCustomer : boolean = true;
    private hasPermission: boolean[] = [] as boolean[];
    private numberOfTickets : number = 1;
    private numberOfAdditionalTickets : number = 10;
    private tokenlistModelList : TokenListView[] = [] as TokenListView[];
    private tokenModelList : TokenView[] | undefined = [] as TokenView[];
    private ticketListToDelete : TokenListModel | undefined;
    private customerModelList : CustomertModel[] | undefined;
    private loading : boolean = true;
    private showTickets : boolean = false;
    private loadingTokens : boolean = true;
    private formTicketlistValid : boolean = false;
    private newTicketlistModel : TokenListModel = {} as TokenListModel;
    private currentTicketlistModel : TokenListModel = {} as TokenListModel;
    private tokenPage : number = 0;
    private tokenCount : number = 25;
    private tokenTableCount : number = 0;
    private ticketListName : string = "";
    private validFrom : Date;
    private validUntil : Date;
    private windowWidth : number = window.innerWidth;
    private windowHeight : number = window.innerHeight;
    private rules =
    {
        name:
        [
            {
                required: true,
                message: 'Bitte geben Sie eine Bezeichnung ein.',
                trigger: 'blur'
            }
        ]
    };

    public async mounted()
    {
        // get permissions
        await AuthService.getPermissions();
        this.hasPermission['TKLS_SELECT'] = AuthService.hasPermission("TKLS_SELECT");
        this.hasPermission['TKLS_INSERT'] = AuthService.hasPermission("TKLS_INSERT");
        this.hasPermission['TKLS_UPDATE'] = AuthService.hasPermission("TKLS_UPDATE");
        this.hasPermission['TKLS_DELETE'] = AuthService.hasPermission("TKLS_DELETE");

        this.isCustomer = await AuthService.isCustomer();

        if (this.hasPermission['TKLS_SELECT'])
        {
            await this.loadCustomerList();

            if (this.customerModelList)
            {
                this.customerId = this.customerModelList[0].customerid;

                await this.loadTicketList();
            }
        }
        
        window.addEventListener('resize', this.onWindowResize);

        this.loading = false;
    }

    private beforeUnmount()
    {
        window.removeEventListener('resize', this.onWindowResize);
    }

	private onWindowResize(e : any)
    {
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
    }

    private async loadCustomerList()
    {
        this.loading = true;
        this.customerModelList = await ApiService.getAllCustomers() as CustomerModel[];
        this.loading = false;
    }

    private async loadTicketList()
    {
        this.loading = true;
        this.tokenlistModelList = await ApiService.getTokenListsForCustomer(this.customerId.toString()) as TokenListView[];
        this.loading = false;
    }

    private async createTicketlist()
    {
		// set loading indicator
		this.loading = true;
        
        // convert selected dates to timestamps
        this.newTicketlistModel.valid_from = ApiService.getTimestampForTimezone(this.validFrom, "Europe/Berlin") as any;
        this.newTicketlistModel.valid_until = ApiService.getTimestampForTimezone(this.validUntil, "Europe/Berlin") as any;

        // send new ticketlist model via request to API and await
        this.newTicketlistModel.customerid = this.customerId;

        let ticketlist = await ApiService.createTokenList(this.newTicketlistModel);

        // check if successful
		if (ticketlist)
		{
			await ApiService.generateTokens(ticketlist.tokenlistid, this.numberOfTickets);

			ElMessage({message: "Neue Ticketliste wurde erfolgreich erstellt.",	type: 'success'});
			this.activeName = "1";
			this.numberOfTickets = 1;
			this.newTicketlistModel = {} as TokenListModel;
            this.validFrom = null;
            this.validUntil = null;
			this.formTicketlistValid = false;
			await this.loadTicketList();
		}
		else
		{
			// or show error message
			ElMessage({message: "Beim Erstellen der neuen Ticketliste ist ein Fehler aufgetreten.",	type: 'error'});
		}
	
		this.loading = false;
    }

    private async addTicketsToList()
    {
		// set loading indicator
		this.loading = true;
        
        // check if a ticketlist is selected
		if (this.currentTicketlistModel)
		{
			await ApiService.generateTokens(this.currentTicketlistModel.tokenlistid, this.numberOfAdditionalTickets);

			ElMessage({message: "Die Ticketliste wurde erfolgreich ergänzt.",	type: 'success'});
			this.numberOfTickets = 1;
			await this.loadTicketList();
            await this.showTicketsForList(this.currentTicketlistModel);
		}
		else
		{
			// or show error message
			ElMessage({message: "Beim Erweitern der Ticketliste ist ein Fehler aufgetreten.",	type: 'error'});
		}
	
		this.loading = false;
    }

    private async deleteTicketlist(ticketlist : TokenListModel)
    {
        let ok = await ApiService.deleteTokenList(ticketlist);

        if (ok)
        {
			ElMessage({message: "Ticketliste wurde gelöscht.",	type: 'success'});
            await this.loadTicketList();
        }
        else
        {
            // or show error message
			ElMessage({message: "Ticketliste konnte nicht gelöscht werden.",	type: 'error'});
        }
    }

    private validateTicketlistForm() : boolean
    {
        if (!this.newTicketlistModel.name || this.newTicketlistModel.name.length <= 0) return false;

        if (!this.numberOfTickets || this.numberOfTickets <= 0) return false;

        if (!this.validFrom) return false;

        if (!this.validUntil) return false;
        
        let nowDate : Date = new Date();
        nowDate.setHours(0,0,0,0);

        if (this.validUntil < nowDate)
        {
			ElMessage({message: "Das Endedatum des Gültigkeitszeitraums liegt in der Vergangenheit.",	type: 'error'});
            return false;
        }

        if (this.validUntil < this.validFrom)
        {
			ElMessage({message: "Das Endedatum des Gültigkeitszeitraums liegt vor dem Anfangsdatum.",	type: 'error'});
            return false;
        }

        return true;
    }

    private async showTicketsForList(ticketlist : TokenListModel)
    {
        this.showTickets = true;

        this.currentTicketlistModel = ticketlist;
        this.loadingTokens = true;
        this.ticketListName = ticketlist.name;
        this.tokenTableCount = await ApiService.getTableCount("token", `tokenlistid = ${ticketlist.tokenlistid.toString()}`);
        this.tokenModelList = await ApiService.getAllTokenForListView(ticketlist.tokenlistid.toString(), this.tokenPage.toString(), this.tokenCount.toString());
        this.loadingTokens = false;
    }
    
    private onTicketListDeleteConfirm(action : string)
    {
        if (this.ticketListToDelete && action == 'confirm')
        {
            this.deleteTicketlist(this.ticketListToDelete);
        }
    }


    private onTicketlistInput(value : number)
    {
        if (value == null) 
        {
            value = 0;
            this.numberOfTickets = 0;
        }

        this.formTicketlistValid = this.validateTicketlistForm();
    }

    private async onPageChange(page : any)
    {
        this.tokenPage = page-1;
        this.loadingTokens = true;
        this.tokenModelList = await ApiService.getAllTokenForListView(this.currentTicketlistModel.tokenlistid.toString(), this.tokenPage.toString(), this.tokenCount.toString());
        this.loadingTokens = false;
    }

    private onShowTicketlist(ticketlist : TokenListModel)
    {
        this.showTicketsForList(ticketlist);
    }

    private onTicketlistInput(value : number)
    {
        if (value == null) 
        {
            value = 0;
            this.numberOfTickets = 0;
        }

        this.formTicketlistValid = this.validateTicketlistForm();
    }

    private async onCustomerChange()
    {
        if (this.customerId > 0)
        {
            await this.loadTicketList();
            this.activeName = "1";
        }
    }
    
    private onTicketlistDeleteClick(ticketlist : TokenListModel)
    {
        this.ticketListToDelete = ticketlist;

        ElMessageBox.confirm('Soll diese Ticketliste wirklich gelöscht werden?', 'Warning', 
        {
            type: 'warning',
            title: '',
            callback: this.onTicketListDeleteConfirm,
            confirmButtonText: 'Löschen',
            cancelButtonText: 'Abbrechen'
        });
    }
    
    private onNewTicketlistClick()
    {
        this.createTicketlist();
    }

    private onAddTicketsClick()
    {
        if (this.currentTicketlistModel)
        {
            this.addTicketsToList();
        }
    }

}
